import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Download,
  MapPin,
  Search,
  ShieldAlert,
  TriangleAlert,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import { campaignBelongsToWorkspace } from '../../lib/workspace';
import { DailyCheckinsExportDialog } from './DailyCheckinsExportDialog';
import type {
  AttendanceReviewEvent,
  AttendanceReviewDecision,
  AttendanceReviewResponse,
  AttendancePendingEvidence,
  Campaign,
  OperationalExceptionResponse,
  OperationalExceptionType,
  PaginatedResponse,
  Venue,
} from '../../types/domain';

interface AttendanceFilters {
  search: string;
  client: string;
  agency: string;
  campaign: string;
  venue: string;
  brandAmbassador: string;
  eventType: string;
  validationStatus: string;
  reviewStatus: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: AttendanceFilters = {
  search: '',
  client: '',
  agency: '',
  campaign: '',
  venue: '',
  brandAmbassador: '',
  eventType: '',
  validationStatus: '',
  reviewStatus: '',
  dateFrom: '',
  dateTo: '',
};

const attendanceDataTabs = [
  { id: 'records', label: 'Attendance records' },
  { id: 'pending-evidence', label: 'Evidence pending' },
  { id: 'missing-checkouts', label: 'Missing checkouts' },
  { id: 'exceptions', label: 'Exceptions' },
] as const;

const attendanceFilterOptionsQueryOptions = {
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
} as const;

type AttendanceDataTab = typeof attendanceDataTabs[number]['id'];
type SortDirection = 'ascending' | 'descending' | 'none';
type MissingCheckoutSortField = 'brandAmbassador' | 'campaignAndStore' | 'scheduledShift';

const missingCheckoutOrderingFields: Record<MissingCheckoutSortField, readonly string[]> = {
  brandAmbassador: ['ba_user__full_name'],
  campaignAndStore: ['assignment__campaign__name', 'shift__location__venue__name'],
  scheduledShift: ['shift__starts_at'],
};

function orderingValue(fields: readonly string[], direction: Exclude<SortDirection, 'none'>) {
  const prefix = direction === 'descending' ? '-' : '';
  return fields.map((field) => `${prefix}${field}`).join(',');
}

const attendanceExceptionTypes: Array<{
  value: Extract<OperationalExceptionType, 'no_effective_check_in' | 'sales_without_effective_attendance'>;
  label: string;
}> = [
  { value: 'no_effective_check_in', label: 'No effective check-in' },
  { value: 'sales_without_effective_attendance', label: 'Sales submitted without effective attendance' },
];

function filtersFromSearchParams(searchParams: URLSearchParams): AttendanceFilters {
  return {
    search: searchParams.get('search') ?? '',
    client: searchParams.get('client') ?? '',
    agency: searchParams.get('agency') ?? '',
    campaign: searchParams.get('campaign') ?? '',
    venue: searchParams.get('venue') ?? '',
    brandAmbassador: searchParams.get('brand_ambassador') ?? '',
    eventType: searchParams.get('event_type') ?? '',
    validationStatus: searchParams.get('validation_status') ?? '',
    reviewStatus: searchParams.get('review_status') ?? '',
    dateFrom: searchParams.get('date_from') ?? '',
    dateTo: searchParams.get('date_to') ?? '',
  };
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

function readableReason(reason: string) {
  return reason ? reason.replaceAll('_', ' ') : 'No exception recorded';
}

function formatDeviceUtcOffset(offsetMinutes: number | null) {
  if (offsetMinutes === null) return 'Not reported';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60).toString().padStart(2, '0');
  const minutes = (absoluteMinutes % 60).toString().padStart(2, '0');
  return `UTC${offsetMinutes <= 0 ? '+' : '-'}${hours}:${minutes}`;
}

function checkinOpensAt(event: AttendanceReviewEvent) {
  return new Date(
    new Date(event.shift_starts_at).getTime() - event.shift_checkin_open_minutes * 60_000,
  );
}

function checkoutDeadlineAt(event: AttendanceReviewEvent) {
  return new Date(
    new Date(event.shift_ends_at).getTime() + event.shift_checkout_grace_minutes * 60_000,
  );
}

function pakistanDateTimeLocal(value: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    timeZone: 'Asia/Karachi',
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

export function AttendancePage() {
  const { accessToken, selectedWorkspace } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const requestedTab = searchParams.get('tab');
  const activeDataTab: AttendanceDataTab = attendanceDataTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab as AttendanceDataTab
    : 'records';
  const requestedExceptionType = searchParams.get('exception_type');
  const attendanceExceptionType = attendanceExceptionTypes.some(
    (type) => type.value === requestedExceptionType,
  )
    ? requestedExceptionType as typeof attendanceExceptionTypes[number]['value']
    : 'no_effective_check_in';
  const [filters, setFilters] = useState<AttendanceFilters>(
    () => filtersFromSearchParams(searchParams),
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => filtersFromSearchParams(searchParams).search.trim(),
  );
  const [page, setPage] = useState(1);
  const [missingCheckoutSort, setMissingCheckoutSort] = useState<{
    field: MissingCheckoutSortField;
    direction: Exclude<SortDirection, 'none'>;
  }>({ field: 'scheduledShift', direction: 'descending' });
  const [selectedEvent, setSelectedEvent] = useState<AttendanceReviewEvent | null>(null);
  const [showDailyExport, setShowDailyExport] = useState(false);
  const [decisionDraft, setDecisionDraft] = useState<{
    event: AttendanceReviewEvent;
    decision: 'approved' | 'rejected';
    note: string;
  } | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [resolutionDraft, setResolutionDraft] = useState<{
    event: AttendanceReviewEvent;
    effectiveCheckoutAt: string;
    note: string;
  } | null>(null);
  const campaigns = useApiCollection<Campaign>(
    'attendance-campaigns',
    '/campaigns/?page_size=100&ordering=-starts_on',
    accessToken,
    true,
    attendanceFilterOptionsQueryOptions,
  );
  const venues = useApiCollection<Venue>(
    'attendance-venues',
    '/venues/?page_size=100&ordering=name',
    accessToken,
    true,
    attendanceFilterOptionsQueryOptions,
  );
  const campaignOptions = useMemo(
    () => (campaigns.data?.results ?? []).filter((campaign) => (
      campaignBelongsToWorkspace(campaign, selectedWorkspace)
    )),
    [campaigns.data?.results, selectedWorkspace],
  );
  const clientOptions = useMemo(
    () => [...new Map(campaignOptions.map((campaign) => [
      campaign.client,
      { id: campaign.client, name: campaign.client_name },
    ])).values()],
    [campaignOptions],
  );
  const agencyOptions = useMemo(
    () => [...new Map(campaignOptions.flatMap((campaign) => (
      campaign.operating_agency
        ? [[campaign.operating_agency, {
            id: campaign.operating_agency,
            name: campaign.operating_agency_name ?? 'Agency',
          }] as const]
        : []
    ))).values()],
    [campaignOptions],
  );
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [filters.search]);
  const reviewPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '25',
      ordering: '-captured_at',
    });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
    if (filters.eventType) params.set('event_type', filters.eventType);
    if (filters.validationStatus) {
      params.set('validation_status', filters.validationStatus);
    }
    if (filters.reviewStatus) params.set('review_status', filters.reviewStatus);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/attendance/review/?${params.toString()}`;
  })();
  const attendance = useQuery({
    queryKey: ['attendance-review', reviewPath, accessToken],
    enabled: Boolean(accessToken) && activeDataTab === 'records',
    queryFn: () => apiRequest<AttendanceReviewResponse>(
      reviewPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const pendingEvidenceParams = new URLSearchParams(reviewPath.split('?')[1]);
  pendingEvidenceParams.delete('validation_status');
  pendingEvidenceParams.delete('review_status');
  pendingEvidenceParams.set('ordering', '-created_at');
  const pendingEvidencePath = `/attendance/pending-evidence/?${pendingEvidenceParams.toString()}`;
  const pendingEvidence = useQuery({
    queryKey: ['attendance-pending-evidence', pendingEvidencePath, accessToken],
    enabled: Boolean(accessToken) && activeDataTab === 'pending-evidence',
    refetchInterval: activeDataTab === 'pending-evidence' ? 30_000 : false,
    queryFn: () => apiRequest<PaginatedResponse<AttendancePendingEvidence>>(
      pendingEvidencePath,
      accessToken ? { accessToken } : {},
    ),
  });
  const exceptionPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '25',
      exception_type: attendanceExceptionType,
    });
    if (selectedWorkspace?.organizationId) params.set('workspace', selectedWorkspace.organizationId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/operational-exceptions/?${params.toString()}`;
  })();
  const exceptions = useQuery({
    queryKey: ['operational-exceptions', exceptionPath, accessToken],
    enabled: Boolean(accessToken) && activeDataTab === 'exceptions',
    refetchInterval: activeDataTab === 'exceptions' ? 30_000 : false,
    queryFn: () => apiRequest<OperationalExceptionResponse>(
      exceptionPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const missingCheckoutPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '25',
      ordering: orderingValue(
        missingCheckoutOrderingFields[missingCheckoutSort.field],
        missingCheckoutSort.direction,
      ),
      missing_checkout: 'true',
    });
    if (selectedWorkspace?.organizationId) params.set('workspace', selectedWorkspace.organizationId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/attendance/review/?${params.toString()}`;
  })();
  const missingCheckouts = useQuery({
    queryKey: ['attendance-missing-checkouts', missingCheckoutPath, accessToken],
    enabled: Boolean(accessToken) && activeDataTab === 'missing-checkouts',
    queryFn: () => apiRequest<AttendanceReviewResponse>(
      missingCheckoutPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const exportParams = new URLSearchParams(reviewPath.split('?')[1]);
  exportParams.delete('page');
  exportParams.delete('page_size');
  const exportPath = `/attendance/review/export/?${exportParams.toString()}`;
  const rows = attendance.data?.results ?? [];
  const missingCheckoutRows = missingCheckouts.data?.results ?? [];
  const exceptionRows = exceptions.data?.results ?? [];
  const pendingEvidenceRows = pendingEvidence.data?.results ?? [];
  const tabCounts = {
    records: attendance.data?.count,
    'pending-evidence': pendingEvidence.data?.count,
    'missing-checkouts': missingCheckouts.data?.count,
    exceptions: exceptions.data?.count,
  };
  const summary = attendance.data?.summary ?? {
    total: 0,
    accepted: 0,
    flagged: 0,
    rejected: 0,
    review_pending: 0,
    review_approved: 0,
    review_rejected: 0,
    checkout_missing: 0,
  };
  const decisionMutation = useMutation({
    mutationFn: (draft: NonNullable<typeof decisionDraft>) => apiRequest<AttendanceReviewDecision>(
      `/attendance/review/${draft.event.id}/decisions/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          decision: draft.decision,
          note: draft.note,
          workspace: selectedWorkspace?.organizationId ?? null,
          idempotency_key: crypto.randomUUID(),
        }),
      },
    ),
    onSuccess: async () => {
      setDecisionDraft(null);
      setDecisionError(null);
      setSelectedEvent(null);
      await queryClient.invalidateQueries({ queryKey: ['attendance-review'] });
      await queryClient.invalidateQueries({ queryKey: ['attendance-missing-checkouts'] });
    },
    onError: (error) => setDecisionError(getApiErrorMessage(error)),
  });
  const resolutionMutation = useMutation({
    mutationFn: (draft: NonNullable<typeof resolutionDraft>) => apiRequest(
      `/attendance/review/${draft.event.id}/missed-checkout-resolution/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          effective_checkout_at: draft.effectiveCheckoutAt,
          note: draft.note,
          workspace: selectedWorkspace?.organizationId ?? null,
          idempotency_key: crypto.randomUUID(),
        }),
      },
    ),
    onSuccess: async () => {
      setResolutionDraft(null);
      setDecisionError(null);
      setSelectedEvent(null);
      await queryClient.invalidateQueries({ queryKey: ['attendance-review'] });
      await queryClient.invalidateQueries({ queryKey: ['attendance-missing-checkouts'] });
    },
    onError: (error) => setDecisionError(getApiErrorMessage(error)),
  });
  const exportMutation = useMutation({
    mutationFn: () => downloadApiFile(
      exportPath,
      accessToken ? { accessToken } : {},
    ),
  });

  function updateFilter<Key extends keyof AttendanceFilters>(
    key: Key,
    value: AttendanceFilters[Key],
  ) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setDebouncedSearch('');
    setPage(1);
  }

  function selectDataTab(tab: AttendanceDataTab) {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'records') {
      nextParams.delete('tab');
      nextParams.delete('exception_type');
    } else {
      nextParams.set('tab', tab);
      if (tab === 'exceptions') nextParams.set('exception_type', attendanceExceptionType);
      else nextParams.delete('exception_type');
    }
    setPage(1);
    setSearchParams(nextParams);
  }

  function selectExceptionType(exceptionType: typeof attendanceExceptionTypes[number]['value']) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'exceptions');
    nextParams.set('exception_type', exceptionType);
    setPage(1);
    setSearchParams(nextParams);
  }

  function openDecision(event: AttendanceReviewEvent, decision: 'approved' | 'rejected') {
    setDecisionError(null);
    setSelectedEvent(null);
    setDecisionDraft({ event, decision, note: '' });
  }

  function openResolution(event: AttendanceReviewEvent) {
    setDecisionError(null);
    setSelectedEvent(null);
    setResolutionDraft({
      event,
      effectiveCheckoutAt: pakistanDateTimeLocal(event.shift_ends_at),
      note: '',
    });
  }

  function toggleMissingCheckoutOrdering(field: MissingCheckoutSortField) {
    setMissingCheckoutSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'ascending'
        ? 'descending'
        : 'ascending',
    }));
    setPage(1);
  }

  function missingCheckoutSortDirection(field: MissingCheckoutSortField): SortDirection {
    return missingCheckoutSort.field === field ? missingCheckoutSort.direction : 'none';
  }

  function sortIcon(direction: SortDirection) {
    if (direction === 'ascending') return <ArrowUp aria-hidden="true" size={14} />;
    if (direction === 'descending') return <ArrowDown aria-hidden="true" size={14} />;
    return <ArrowUpDown aria-hidden="true" size={14} />;
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Attendance</h1>
          <p>Review GPS and selfie-backed check-ins, checkouts, validation exceptions, and decisions.</p>
        </div>
        <div className="heading-actions">
          {activeDataTab === 'records' ? <button className="button secondary export-button" onClick={() => setShowDailyExport(true)} type="button"><Download size={16} /> Daily check-ins XLSX</button> : null}
          {activeDataTab === 'records' ? <button className="button secondary export-button" disabled={exportMutation.isPending || summary.total === 0} onClick={() => exportMutation.mutate()} type="button">
            <Download size={16} /> {exportMutation.isPending ? 'Preparing CSV…' : 'Export CSV'}
          </button> : null}
          <span className="record-count"><ClipboardCheck size={16} /> {activeDataTab === 'records' ? `${summary.total} events` : activeDataTab === 'pending-evidence' ? `${pendingEvidence.data?.count ?? 0} pending attempts` : activeDataTab === 'missing-checkouts' ? `${missingCheckouts.data?.count ?? 0} missing checkouts` : `${exceptions.data?.count ?? 0} exceptions`}</span>
        </div>
      </header>
      {activeDataTab === 'records' && exportMutation.isError ? <p className="form-error page-error" role="alert">{getApiErrorMessage(exportMutation.error)}</p> : null}
      {showDailyExport ? <DailyCheckinsExportDialog
        accessToken={accessToken}
        filters={exportParams.toString()}
        initialDate={filters.dateFrom && filters.dateFrom === filters.dateTo ? filters.dateFrom : ''}
        key={selectedWorkspace?.id}
        onClose={() => setShowDailyExport(false)}
      /> : null}

      {activeDataTab === 'records' ? <section className="metric-grid attendance-metrics" aria-label="Attendance summary">
        <article className="metric-card"><span className="icon-chip"><Clock3 size={18} /></span><p>Total events</p><strong>{summary.total}</strong><small>Matching the current filters</small></article>
        <article className="metric-card"><span className="icon-chip"><CheckCircle2 size={18} /></span><p>Approved</p><strong>{summary.review_approved}</strong><small>Automatic or human approval</small></article>
        <article className="metric-card"><span className="icon-chip attendance-warning"><TriangleAlert size={18} /></span><p>Pending review</p><strong>{summary.review_pending}</strong><small>Needs an authorised decision</small></article>
        <article className="metric-card"><span className="icon-chip attendance-danger"><XCircle size={18} /></span><p>Rejected</p><strong>{summary.review_rejected}</strong><small>Server or human rejection</small></article>
        <article className="metric-card"><span className="icon-chip attendance-warning"><Clock3 size={18} /></span><p>Missing checkout</p><strong>{summary.checkout_missing}</strong><small>Expired open shifts requiring resolution</small></article>
      </section> : null}

      <section className="content-card attendance-filter-card">
        <div className="card-heading">
          <div><h2>{activeDataTab === 'records' ? 'Filter attendance' : activeDataTab === 'pending-evidence' ? 'Filter pending evidence' : activeDataTab === 'missing-checkouts' ? 'Filter missing checkouts' : 'Filter attendance exceptions'}</h2><p>{activeDataTab === 'records' ? 'Results and summary cards use the same authorised scope.' : activeDataTab === 'pending-evidence' ? 'Received attempts awaiting GPS and selfie evidence. Dates filter the original device capture time.' : activeDataTab === 'missing-checkouts' ? 'Expired effective check-ins without a BA-recorded checkout, including manager-resolved cases.' : 'Expected active work is evaluated against effective attendance and submitted sales.'}</p></div>
          <button className="text-button" onClick={resetFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid">
          <label className="attendance-search-filter">
            Brand ambassador, campaign or store
            <span><Search size={16} /><input aria-label="Brand ambassador, campaign or store" onChange={(event) => updateFilter('search', event.target.value)} placeholder="Search by name" value={filters.search} /></span>
          </label>
          <label>
            Client
            <select aria-label="Attendance client" onChange={(event) => updateFilter('client', event.target.value)} value={filters.client}>
              <option value="">All clients</option>
              {clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
          <label>
            Agency
            <select aria-label="Attendance agency" onChange={(event) => updateFilter('agency', event.target.value)} value={filters.agency}>
              <option value="">All agencies</option>
              {agencyOptions.map((agency) => <option key={agency.id} value={agency.id}>{agency.name}</option>)}
            </select>
          </label>
          <label>
            Campaign
            <select onChange={(event) => updateFilter('campaign', event.target.value)} value={filters.campaign}>
              <option value="">All campaigns</option>
              {campaignOptions.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
            </select>
          </label>
          <label>
            Store
            <StorePicker
              allOptionLabel="All stores"
              ariaLabel="Attendance store"
              onChange={(value) => updateFilter('venue', value)}
              options={(venues.data?.results ?? []).map((venue) => ({
                id: venue.id,
                name: venue.name,
                address: venue.address,
                city: venue.city,
                chainName: venue.chain_name,
                storeCode: venue.store_code,
              }))}
              value={filters.venue}
            />
          </label>
          {activeDataTab === 'records' || activeDataTab === 'pending-evidence' ? <label>
            Event
            <select onChange={(event) => updateFilter('eventType', event.target.value)} value={filters.eventType}>
              <option value="">Check-in and checkout</option>
              <option value="check_in">Check-in</option>
              <option value="check_out">Checkout</option>
            </select>
          </label> : null}
          {activeDataTab === 'records' ? <label>
            Validation
            <select onChange={(event) => updateFilter('validationStatus', event.target.value)} value={filters.validationStatus}>
              <option value="">All validation results</option>
              <option value="accepted">Accepted</option>
              <option value="flagged">Flagged</option>
              <option value="rejected">Rejected</option>
            </select>
          </label> : null}
          {activeDataTab === 'records' ? <label>
            Review
            <select onChange={(event) => updateFilter('reviewStatus', event.target.value)} value={filters.reviewStatus}>
              <option value="">All review results</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label> : null}
          <label>Date from<input max={filters.dateTo || undefined} onChange={(event) => updateFilter('dateFrom', event.target.value)} type="date" value={filters.dateFrom} /></label>
          <label>Date to<input min={filters.dateFrom || undefined} onChange={(event) => updateFilter('dateTo', event.target.value)} type="date" value={filters.dateTo} /></label>
        </div>
      </section>

      <nav aria-label="Attendance data sections" className="campaign-setup-tabs attendance-data-tabs" role="tablist">
        {attendanceDataTabs.map((tab) => (
          <button
            aria-controls={`attendance-data-panel-${tab.id}`}
            aria-selected={activeDataTab === tab.id}
            className={activeDataTab === tab.id ? 'active' : ''}
            id={`attendance-data-tab-${tab.id}`}
            key={tab.id}
            onClick={() => selectDataTab(tab.id)}
            role="tab"
            type="button"
          >
            <span>{tab.label}</span>
            {tabCounts[tab.id] !== undefined ? <small>{tabCounts[tab.id]}</small> : null}
          </button>
        ))}
      </nav>

      {activeDataTab === 'records' ? <div aria-labelledby="attendance-data-tab-records" id="attendance-data-panel-records" role="tabpanel">
      <DataPanel loading={attendance.isLoading} error={attendance.isError} empty={rows.length === 0}>
        <section className="content-card data-card attendance-table-card">
          <div className="data-table-wrap">
            <table className="data-table attendance-table">
              <colgroup>
                <col className="attendance-col-ambassador" />
                <col className="attendance-col-campaign" />
                <col className="attendance-col-event" />
                <col className="attendance-col-gps" />
                <col className="attendance-col-validation" />
                <col className="attendance-col-review" />
                <col className="attendance-col-evidence" />
              </colgroup>
              <thead><tr><th>Brand ambassador</th><th>Campaign and store</th><th>Event</th><th>GPS evidence</th><th>Validation</th><th>Review</th><th>Evidence</th></tr></thead>
              <tbody>
                {rows.map((event) => (
                  <tr key={event.id}>
                    <td><strong>{event.brand_ambassador_name}</strong><small>{event.assignment_reference} · {event.hired_by_name}</small></td>
                    <td><strong>{event.campaign_name}</strong><small><MapPin size={12} /> {event.venue_name} · {event.venue_city}</small></td>
                    <td><strong>{event.event_type === 'check_in' ? 'Check-in' : 'Checkout'}</strong><small>{event.reference} · {dateTimeFormatter.format(new Date(event.captured_at))}</small><small>Attendance recorded</small>{event.checkout_missing ? <small className="attendance-missing-label">Checkout missing—requires review</small> : null}</td>
                    <td><strong>{Number(event.distance_from_site_m).toFixed(1)} m from store</strong><small>Accuracy ±{Number(event.horizontal_accuracy_m).toFixed(1)} m</small></td>
                    <td><StatusPill value={event.validation_status} /><small>{readableReason(event.display_validation_reason ?? event.validation_reason)}</small></td>
                    <td><StatusPill value={event.review_status} /><small>{event.review_source === 'human' ? 'Human decision' : event.review_source === 'automatic' ? 'Server decision' : 'Awaiting review'}</small></td>
                    <td><button className="button secondary attendance-selfie-button" onClick={() => setSelectedEvent(event)} type="button"><Camera size={15} /> View selfie</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination">
            <span>Page {page} · {attendance.data?.count ?? 0} matching events</span>
            <div>
              <button className="button secondary" disabled={!attendance.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
              <button className="button secondary" disabled={!attendance.data?.next} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
            </div>
          </div>
        </section>
      </DataPanel>
      </div> : activeDataTab === 'pending-evidence' ? <div aria-labelledby="attendance-data-tab-pending-evidence" id="attendance-data-panel-pending-evidence" role="tabpanel">
        <section className="content-card exception-type-card">
          <p>Attempt received; evidence pending. These attempts are not recorded attendance, do not count as presence, and cannot be approved. Once evidence is processed, check Attendance records for validation and review. This list refreshes every 30 seconds; expired attempts are excluded.</p>
          <button className="button secondary" disabled={pendingEvidence.isFetching} onClick={() => { void pendingEvidence.refetch(); }} type="button">Refresh</button>
        </section>
        <DataPanel loading={pendingEvidence.isLoading} error={pendingEvidence.isError} empty={false}>
          {pendingEvidenceRows.length === 0 ? <section className="content-card data-state">No pending evidence matches these filters.</section> : <section className="content-card data-card attendance-table-card">
            <div className="data-table-wrap">
              <table className="data-table attendance-pending-table">
                <thead><tr><th>Brand ambassador</th><th>Campaign and store</th><th>Scheduled shift</th><th>Attempt</th><th>Submission status</th></tr></thead>
                <tbody>{pendingEvidenceRows.map((attempt) => <tr key={attempt.id}>
                  <td><strong>{attempt.brand_ambassador_name}</strong><small>{attempt.assignment_reference}</small></td>
                  <td><strong>{attempt.campaign_name}</strong><small><MapPin size={12} /> {attempt.venue_name} · {attempt.venue_city}</small></td>
                  <td><strong>{dateTimeFormatter.format(new Date(attempt.shift_starts_at))}</strong><small>Ends {dateTimeFormatter.format(new Date(attempt.shift_ends_at))}</small></td>
                  <td><strong>{attempt.event_type === 'check_in' ? 'Check-in' : 'Checkout'}</strong><small>Captured {dateTimeFormatter.format(new Date(attempt.captured_at))}</small><small>Received {dateTimeFormatter.format(new Date(attempt.received_at))}</small></td>
                  <td><StatusPill value="evidence_pending" /><small>Not recorded attendance</small><small>Expires {dateTimeFormatter.format(new Date(attempt.expires_at))}</small></td>
                </tr>)}</tbody>
              </table>
            </div>
            <div className="attendance-pagination">
              <span>Page {page} · {pendingEvidence.data?.count ?? 0} pending attempts</span>
              <div><button className="button secondary" disabled={!pendingEvidence.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button><button className="button secondary" disabled={!pendingEvidence.data?.next} onClick={() => setPage((current) => current + 1)} type="button">Next</button></div>
            </div>
          </section>}
        </DataPanel>
      </div> : activeDataTab === 'missing-checkouts' ? <div aria-labelledby="attendance-data-tab-missing-checkouts" id="attendance-data-panel-missing-checkouts" role="tabpanel">
        <DataPanel loading={missingCheckouts.isLoading} error={missingCheckouts.isError} empty={missingCheckoutRows.length === 0}>
          <section className="content-card data-card attendance-table-card">
            <div className="data-table-wrap missing-checkouts-table-wrap">
              <table className="data-table missing-checkouts-table">
                <thead><tr>
                  <th aria-sort={missingCheckoutSortDirection('brandAmbassador')}><button aria-label="Sort missing checkouts by brand ambassador" className="table-sort-button" onClick={() => toggleMissingCheckoutOrdering('brandAmbassador')} type="button">Brand ambassador {sortIcon(missingCheckoutSortDirection('brandAmbassador'))}</button></th>
                  <th aria-sort={missingCheckoutSortDirection('campaignAndStore')}><button aria-label="Sort missing checkouts by campaign and store" className="table-sort-button" onClick={() => toggleMissingCheckoutOrdering('campaignAndStore')} type="button">Campaign and store {sortIcon(missingCheckoutSortDirection('campaignAndStore'))}</button></th>
                  <th aria-sort={missingCheckoutSortDirection('scheduledShift')}><button aria-label="Sort missing checkouts by scheduled shift" className="table-sort-button" onClick={() => toggleMissingCheckoutOrdering('scheduledShift')} type="button">Scheduled shift {sortIcon(missingCheckoutSortDirection('scheduledShift'))}</button></th>
                  <th>Checkout status</th><th>Resolution</th><th>Evidence</th>
                </tr></thead>
                <tbody>{missingCheckoutRows.map((event) => {
                  const resolution = event.checkout_resolution;
                  return <tr key={event.id}>
                    <td><strong>{event.brand_ambassador_name}</strong><small>{event.assignment_reference} · {event.hired_by_name}</small></td>
                    <td><strong>{event.campaign_name}</strong><small><MapPin size={12} /> {event.venue_name} · {event.venue_city}</small></td>
                    <td><strong>{dateTimeFormatter.format(new Date(event.shift_starts_at))}</strong><small>Ended {dateTimeFormatter.format(new Date(event.shift_ends_at))}</small></td>
                    <td><StatusPill value={resolution ? 'resolved' : 'unresolved'} /><small>{resolution ? 'Resolved by manager' : 'Not resolved'}</small></td>
                    <td>{resolution ? <><strong>{dateTimeFormatter.format(new Date(resolution.effective_checkout_at))}</strong><small>{resolution.reviewer_name} · {resolution.note}</small></> : <><strong>Action required</strong><small>Checkout deadline has passed</small></>}</td>
                    <td><button className="button secondary attendance-selfie-button" onClick={() => setSelectedEvent(event)} type="button"><Camera size={15} /> Review check-in</button></td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
            <div className="attendance-pagination">
              <span>Page {page} · {missingCheckouts.data?.count ?? 0} missing checkouts</span>
              <div><button className="button secondary" disabled={!missingCheckouts.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button><button className="button secondary" disabled={!missingCheckouts.data?.next} onClick={() => setPage((current) => current + 1)} type="button">Next</button></div>
            </div>
          </section>
        </DataPanel>
      </div> : <div aria-labelledby="attendance-data-tab-exceptions" id="attendance-data-panel-exceptions" role="tabpanel">
        <section className="content-card exception-type-card">
          <label>
            Exception type
            <select aria-label="Attendance exception type" onChange={(event) => selectExceptionType(event.target.value as typeof attendanceExceptionTypes[number]['value'])} value={attendanceExceptionType}>
              {attendanceExceptionTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <p>{attendanceExceptionType === 'no_effective_check_in' ? 'Expected shifts whose check-in deadline has passed without effective check-in evidence.' : 'Sales or a no-sales attestation exists, but there is no effective check-in for that shift.'}</p>
        </section>
        <DataPanel loading={exceptions.isLoading} error={exceptions.isError} empty={exceptionRows.length === 0}>
          <section className="content-card data-card attendance-table-card">
            <div className="data-table-wrap">
              <table className="data-table operational-exceptions-table">
                <thead><tr><th>Brand ambassador</th><th>Campaign and store</th><th>Scheduled shift</th><th>Check-in submission</th><th>Sales report</th></tr></thead>
                <tbody>{exceptionRows.map((item) => <tr key={`${item.assignment_id}-${item.shift_id}`}>
                  <td><strong>{item.brand_ambassador_name}</strong><small>{item.brand_ambassador_reference} · {item.assignment_reference}</small></td>
                  <td><strong>{item.campaign_name}</strong><small><MapPin size={12} /> {item.venue_name} · {item.venue_city}</small></td>
                  <td><strong>{dateTimeFormatter.format(new Date(item.shift_starts_at))}</strong><small>Ends {dateTimeFormatter.format(new Date(item.shift_ends_at))}</small></td>
                  <td>{item.check_in_submission_status ? <StatusPill value={item.check_in_submission_status} /> : <small>Submission status unavailable</small>}{item.check_in_submission_status === 'evidence_pending' ? <small>Attempt received; evidence pending</small> : item.check_in_submission_status === 'attempt_expired' ? <small>Evidence was not finalized before expiry</small> : item.check_in_submission_status === 'no_attempt_received' ? <small>No saved check-in attempt or record</small> : null}<small>{item.has_effective_check_in ? item.has_effective_check_out ? 'Check-in and checkout effective' : 'Check-in effective; checkout outstanding' : 'No effective check-in'}</small></td>
                  <td><StatusPill value={item.has_sales_submission ? 'submitted' : 'pending'} /><small>{item.has_sales_submission ? 'Report submitted' : 'No report submitted'}</small></td>
                </tr>)}</tbody>
              </table>
            </div>
            <div className="attendance-pagination">
              <span>Page {page} · {exceptions.data?.count ?? 0} matching exceptions</span>
              <div><button className="button secondary" disabled={!exceptions.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button><button className="button secondary" disabled={!exceptions.data?.next} onClick={() => setPage((current) => current + 1)} type="button">Next</button></div>
            </div>
          </section>
        </DataPanel>
      </div>}

      {selectedEvent ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="attendance-evidence-title" aria-modal="true" className="confirmation-dialog attendance-evidence-dialog" role="dialog">
            <button aria-label="Close selfie" className="icon-button confirmation-close" onClick={() => setSelectedEvent(null)} type="button"><X size={19} /></button>
            <div className="attendance-evidence-heading">
              <div className="confirmation-icon"><Camera size={22} /></div>
              <div><p className="eyebrow">{selectedEvent.reference}</p><h2 id="attendance-evidence-title">Attendance evidence</h2></div>
            </div>
            <img alt={`${selectedEvent.brand_ambassador_name} attendance selfie`} className="attendance-selfie-preview" src={selectedEvent.selfie} />
            <div className="attendance-evidence-meta">
              <span><strong>{selectedEvent.brand_ambassador_name}</strong><small>{selectedEvent.event_type === 'check_in' ? 'Check-in' : 'Checkout'} · {dateTimeFormatter.format(new Date(selectedEvent.captured_at))}</small></span>
              <StatusPill value={selectedEvent.validation_status} />
            </div>
            <p className="confirmation-note">{readableReason(selectedEvent.display_validation_reason ?? selectedEvent.validation_reason)}</p>
            {selectedEvent.display_validation_reason && selectedEvent.display_validation_reason !== selectedEvent.validation_reason ? <p className="confirmation-note">Originally recorded reason: {readableReason(selectedEvent.validation_reason)}.</p> : null}
            <p className="confirmation-note"><MapPin size={13} /> {selectedEvent.venue_name}: {Number(selectedEvent.distance_from_site_m).toFixed(1)} m away with ±{Number(selectedEvent.horizontal_accuracy_m).toFixed(1)} m reported accuracy.</p>
            <div className="attendance-integrity-grid">
              <span><small>Mock-location signal</small><strong>{selectedEvent.location_mocked === true ? 'Detected' : selectedEvent.location_mocked === false ? 'Not detected' : 'Not reported'}</strong></span>
              <span><small>GPS samples</small><strong>{selectedEvent.location_sample_count ?? 'Not reported'}</strong></span>
              <span><small>Maximum drift</small><strong>{selectedEvent.location_max_drift_m == null ? 'Not reported' : `${Number(selectedEvent.location_max_drift_m).toFixed(1)} m`}</strong></span>
              <span><small>Review result</small><StatusPill value={selectedEvent.review_status} /></span>
            </div>
            <section aria-labelledby="attendance-timing-audit-title" className="attendance-timing-audit">
              <h3 id="attendance-timing-audit-title">Attendance timing</h3>
              {(attendance.data?.evaluated_at ?? missingCheckouts.data?.evaluated_at) ? <p>Review data evaluated by the server at {dateTimeFormatter.format(new Date(attendance.data?.evaluated_at ?? missingCheckouts.data?.evaluated_at ?? ''))}.</p> : null}
              <div className="attendance-timing-grid">
                <span><small>Scheduled start</small><strong>{dateTimeFormatter.format(new Date(selectedEvent.shift_starts_at))}</strong></span>
                <span><small>Scheduled end</small><strong>{dateTimeFormatter.format(new Date(selectedEvent.shift_ends_at))}</strong></span>
                <span><small>Check-in opens</small><strong>{dateTimeFormatter.format(checkinOpensAt(selectedEvent))}</strong></span>
                <span><small>Checkout deadline</small><strong>{dateTimeFormatter.format(checkoutDeadlineAt(selectedEvent))}</strong></span>
                <span><small>Captured on device</small><strong>{dateTimeFormatter.format(new Date(selectedEvent.captured_at))}</strong></span>
                <span><small>Received by server</small><strong>{dateTimeFormatter.format(new Date(selectedEvent.received_at))}</strong></span>
                <span><small>Mobile app</small><strong>{selectedEvent.app_version || 'Not reported'}</strong></span>
                <span><small>Device UTC offset</small><strong>{formatDeviceUtcOffset(selectedEvent.device_time_offset_minutes)}</strong></span>
              </div>
            </section>
            {selectedEvent.location_mocked === true ? <p className="attendance-integrity-warning"><ShieldAlert size={15} /> Android reported a mock location. Treat this event as untrusted evidence.</p> : null}
            {selectedEvent.checkout_missing ? <p className="attendance-missing-warning"><TriangleAlert size={15} /> Checkout missing—requires review. The BA history remains unchanged until an authorised manager records a resolution.</p> : null}
            {selectedEvent.checkout_resolution ? <p className="attendance-resolution-note"><CheckCircle2 size={15} /> Missing checkout resolved by {selectedEvent.checkout_resolution.reviewer_name} for {dateTimeFormatter.format(new Date(selectedEvent.checkout_resolution.effective_checkout_at))}.</p> : null}
            {selectedEvent.review_decisions.length > 0 ? (
              <div className="attendance-decision-history">
                <h3>Decision history</h3>
                {selectedEvent.review_decisions.map((decision) => (
                  <article key={decision.id}>
                    <StatusPill value={decision.decision} />
                    <span><strong>{decision.reviewer_name}</strong><small>{decision.reviewer_organization_name ?? 'Platform-wide authority'} · {dateTimeFormatter.format(new Date(decision.created_at))}</small></span>
                    {decision.note ? <p>{decision.note}</p> : null}
                  </article>
                ))}
              </div>
            ) : null}
            <div className="confirmation-actions attendance-review-actions">
              <button className="button secondary" onClick={() => setSelectedEvent(null)} type="button">Close</button>
              {selectedEvent.can_decide ? <button className="button danger" onClick={() => openDecision(selectedEvent, 'rejected')} type="button">Reject</button> : null}
              {selectedEvent.can_decide ? <button className="button primary" onClick={() => openDecision(selectedEvent, 'approved')} type="button">Approve</button> : null}
              {selectedEvent.can_resolve_missing_checkout ? <button className="button primary" onClick={() => openResolution(selectedEvent)} type="button">Resolve missing checkout</button> : null}
            </div>
          </section>
        </div>
      ) : null}

      {decisionDraft ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="attendance-decision-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close decision" className="icon-button confirmation-close" disabled={decisionMutation.isPending} onClick={() => setDecisionDraft(null)} type="button"><X size={19} /></button>
            <div className="confirmation-icon"><ClipboardCheck size={22} /></div>
            <h2 id="attendance-decision-title">{decisionDraft.decision === 'approved' ? 'Approve attendance' : 'Reject attendance'}</h2>
            <p><strong>{decisionDraft.event.reference}</strong> · {decisionDraft.event.brand_ambassador_name} at {decisionDraft.event.venue_name}</p>
            <label className="attendance-decision-note">
              Review note {decisionDraft.decision === 'rejected' || decisionDraft.event.validation_status === 'rejected' ? '(required)' : '(optional)'}
              <textarea autoFocus onChange={(event) => setDecisionDraft((current) => current ? { ...current, note: event.target.value } : null)} placeholder="Record how this decision was verified" rows={4} value={decisionDraft.note} />
            </label>
            <p className="confirmation-note">The GPS, selfie, timestamps, and server validation remain unchanged. This creates a new audited decision.</p>
            {decisionError ? <p className="form-error">{decisionError}</p> : null}
            <div className="confirmation-actions">
              <button className="button secondary" disabled={decisionMutation.isPending} onClick={() => setDecisionDraft(null)} type="button">Cancel</button>
              <button className={decisionDraft.decision === 'approved' ? 'button primary' : 'button danger'} disabled={decisionMutation.isPending} onClick={() => decisionMutation.mutate(decisionDraft)} type="button">{decisionMutation.isPending ? 'Saving…' : decisionDraft.decision === 'approved' ? 'Confirm approval' : 'Confirm rejection'}</button>
            </div>
          </section>
        </div>
      ) : null}

      {resolutionDraft ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="missed-checkout-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close resolution" className="icon-button confirmation-close" disabled={resolutionMutation.isPending} onClick={() => setResolutionDraft(null)} type="button"><X size={19} /></button>
            <div className="confirmation-icon"><Clock3 size={22} /></div>
            <h2 id="missed-checkout-title">Resolve missing checkout</h2>
            <p><strong>{resolutionDraft.event.reference}</strong> · {resolutionDraft.event.brand_ambassador_name} at {resolutionDraft.event.venue_name}</p>
            <label>
              Effective checkout time
              <input max={pakistanDateTimeLocal(new Date().toISOString())} onChange={(event) => setResolutionDraft((current) => current ? { ...current, effectiveCheckoutAt: event.target.value } : null)} type="datetime-local" value={resolutionDraft.effectiveCheckoutAt} />
            </label>
            <label className="attendance-decision-note">
              Resolution note (required)
              <textarea onChange={(event) => setResolutionDraft((current) => current ? { ...current, note: event.target.value } : null)} placeholder="Record how the checkout time was verified" rows={4} value={resolutionDraft.note} />
            </label>
            <p className="confirmation-note">This adds an audited resolution. It does not alter or delete the original check-in evidence.</p>
            {decisionError ? <p className="form-error">{decisionError}</p> : null}
            <div className="confirmation-actions">
              <button className="button secondary" disabled={resolutionMutation.isPending} onClick={() => setResolutionDraft(null)} type="button">Cancel</button>
              <button className="button primary" disabled={resolutionMutation.isPending || !resolutionDraft.effectiveCheckoutAt || !resolutionDraft.note.trim()} onClick={() => resolutionMutation.mutate(resolutionDraft)} type="button">{resolutionMutation.isPending ? 'Saving…' : 'Save resolution'}</button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
